import { Map, fromJS, List, Set } from 'immutable'
import { createSelector } from 'reselect'
import {
  createDeepEqualSelectorMutable,
  createDeepEqualSelector,
} from 'selectors/utils'
import {
  getIn,
  getInOr,
  getInEq,
  getEq,
  findKeysBy,
  safeToJS,
} from 'utils/immutable'
import { getCurrentFeature } from 'selectors/feature/features'
import { queryConditionsSelector } from 'selectors/feature/queries'
import { getFeaturesRouteMatch } from 'selectors/router'
import {
  pick,
  equals,
  identity,
  complement,
  pathOr,
  prop,
  curry,
  compose,
  head,
  propOr,
  flatten,
  path,
  pluck,
  values,
} from 'ramda'
import { isNativeOutput } from 'utils/types/features'
import { getFunctionGraph } from 'reducers/feature/functionGraphs'

const notEquals = complement(equals)
const pluckOutputQueries = compose(pluck('outputQuery'), values)

const queriesSelector = getInOr(Map(), ['feature', 'currentFeature', 'queries'])
const outputMapsSelector = getIn(['feature', 'currentFeature', 'outputMaps'])
const conditionsSelector = getInOr(Map(), [
  'feature',
  'currentFeature',
  'conditions',
])

const computeFunctionGraph = (feature, fnUri) => {
  const { queries = {}, outputMaps, outputs, conditions = {} } = feature

  const outputQueryUris = pluckOutputQueries(outputMaps)

  // single queries are in the conditions object, get those here
  const singleQueries = pick(outputQueryUris, conditions)

  const mergedQueries = {
    ...singleQueries,
    ...queries,
  }

  const outputQueryUri = pathOr('', [fnUri, 'outputQuery'], outputMaps)

  const firstOutputId = getFirstOutputId(fnUri, outputMaps)
  const outputType = getOutputType(firstOutputId, outputs)

  const isFirstOutputTypeNative = isNativeOutput(outputType)

  return getFunctionGraph(
    isFirstOutputTypeNative,
    outputQueryUri,
    conditions,
    mergedQueries
  )
}

const getFirstOutputId = curry((uri, outputMaps) =>
  compose(head, pathOr([], [uri, 'outputs']))(outputMaps)
)

const getOutputType = curry((firstOutputId, outputs) =>
  compose(propOr(false, 'type'), prop(firstOutputId))(outputs)
)
export const getAllFunctionGraphs = createSelector(
  getCurrentFeature,
  getFeaturesRouteMatch,
  (_, props) => props,
  (feature, routeMatch = {}, props) => {
    const featureId = propOr(
      pathOr(
        Object.keys(feature.feature)[0],
        ['params', 'featureId'],
        routeMatch
      ),
      'featureId',
      props
    )
    const featureMeta = path(['feature', featureId], feature)
    const functionUris = propOr([], 'functions', featureMeta)

    const allFeatureColumns = functionUris.reduce((acc, fnUri) => {
      const { columns = [] } = computeFunctionGraph(feature, fnUri) || {}

      const drawn = Object.entries({ ...acc }).reduce((acc, [key, value]) => {
        const uris = flatten(value).map(prop('uri'))
        return [...acc, ...uris]
      }, [])
      const undrawnColumns = columns
        // filter columns with uris that are already in drawn
        .reduce((undrawn, columnGroup) => {
          const undrawnColumn = columnGroup.filter(({ uri }) => {
            return !drawn.includes(uri)
          })
          return [...undrawn, undrawnColumn]
        }, [])
      return {
        ...acc,
        [fnUri]: undrawnColumns.reverse(),
      }
    }, {})
    return allFeatureColumns
  }
)
const selectQueryUrisFromCols = compose(fromJS, pluck('uri'), flatten, values)

const getAllGraphNodeUris = createSelector(
  getAllFunctionGraphs,
  selectQueryUrisFromCols
)

export const getFnColumns = () =>
  createDeepEqualSelectorMutable(
    getAllFunctionGraphs,
    (_, props) => props,
    (allColumns, { id }) => allColumns[id]
  )

export const allFuncGraphNodeUrisSelector = createSelector(
  getFnColumns(),
  selectQueryUrisFromCols
)

export const getAllFuncGraphNodeUris = createDeepEqualSelector(
  allFuncGraphNodeUrisSelector,
  safeToJS
)

const boxPositionsSelector = () => {
  const getFunctionColumns = getFnColumns()

  return createSelector(getFunctionColumns, (columns = []) => {
    const positions = columns.reduce((obj, column = [], columnPosition) => {
      if (!column) return obj
      column.forEach((node, positionInColumn) => {
        obj[node.uri] = {
          columnPosition,
          columnLength: column.length,
          positionInColumn,
        }
      })
      return obj
    }, {})

    return positions
  })
}

const getBoxPositions = () => {
  const boxPositions = boxPositionsSelector()

  return createSelector(
    boxPositions,
    (_, props) => props,
    (positions, { parentQueryUri, subQueryUri }) => ({
      parentColumnBoxCount: (positions[parentQueryUri] || {}).columnLength,
      parentBoxPosition: (positions[parentQueryUri] || {}).positionInColumn,
      childColumnBoxCount: (positions[subQueryUri] || {}).columnLength,
      childBoxPosition: (positions[subQueryUri] || {}).positionInColumn,
      childBoxColumnPosition: (positions[subQueryUri] || {}).columnPosition,
    })
  )
}

const getimportedEntities = createSelector(
  getInOr(Set(), ['feature', 'currentFeature', 'importedEntities']),
  identity
)

const orphanQueryUrisSelector = createDeepEqualSelector(
  getAllGraphNodeUris,
  queriesSelector,
  getimportedEntities,
  (graphNodeUris, queries, importedQueryUris) => {
    // orphans are the queries not found in any function graphs
    return queries.reduce((accum, query, uri) => {
      if (!importedQueryUris.has(uri) && !graphNodeUris.includes(uri)) {
        return accum.push(uri)
      }
      return accum
    }, List())
  }
)

const orphanQuerySelector = createSelector(
  getAllGraphNodeUris,
  queriesSelector,
  (graphNodeUris, queries) =>
    queries.filter((query) => !graphNodeUris.includes(query.get('uri')))
)

const getOrphanQueryUris = createDeepEqualSelector(
  orphanQueryUrisSelector,
  safeToJS
)

const getHasOrphanQueries = createSelector(
  orphanQueryUrisSelector,
  (uris) => !uris.isEmpty()
)

// tracks if there are references to logicSet from other query sets.
// If this number for a given uri-key is more than 1,
// there will be a reference-from symbol next to it's header.
const showLogicNodeCarry = createSelector(
  conditionsSelector,
  queryConditionsSelector,
  outputMapsSelector,
  orphanQueryUrisSelector,
  (_, props) => props,
  (
    conditions,
    conditionsUris,
    outputMaps,
    orphanQueryUris,
    { queryUri, fnId }
  ) => {
    const outputQueryFunctions = findKeysBy(
      getEq('outputQuery', queryUri),
      outputMaps
    )
    if (outputQueryFunctions.some(notEquals(fnId))) return true

    // In the case of the first reference found, we don't want to show the carry,
    // because we are showing a connecting line instead
    let isSecondReferenceInFunction = false

    return conditions.some((condition) => {
      const refQueryUri = condition.get('refQueryUri')
      const conditionUri = condition.get('uri')

      const isSelfReference = conditionsUris.includes(conditionUri)
      const isNotReference = refQueryUri !== queryUri

      if (isNotReference || isSelfReference) return false

      const isReferenceOutsideFunction = !getInEq(
        [conditionUri, 'fnId'],
        fnId,
        conditions
      )
      const isInOrphanSection = orphanQueryUris.includes(refQueryUri)

      if (
        isReferenceOutsideFunction ||
        isSecondReferenceInFunction ||
        isInOrphanSection
      )
        return true

      isSecondReferenceInFunction = true
      return false
    })
  }
)

const makeShowLogicNodeCarry = () => showLogicNodeCarry

export {
  getOrphanQueryUris,
  getHasOrphanQueries,
  getBoxPositions,
  makeShowLogicNodeCarry,
  orphanQuerySelector,
}
